import copy from "copy-to-clipboard";
import {
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  ChevronUp,
  CopyIcon,
  Filter,
  CheckCircle2,
  User,
  Search,
  Plus,
  MoreVertical,
  Folder,
  Flag,
} from "lucide-react";
import React, { useEffect, useState, useMemo } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import { ColumnDef } from "@tanstack/react-table";

import { AddButton } from "../../../../components/Button";
import CustomDropdown from "../../../../components/DropDown";
import Loader from "../../../../components/Loader";
import Modal from "../../../../components/Model";
import ModernTable from "../../../../components/ModernTable/Table";
import VideoModal from "../../../../components/VideoModal";
import EditImg from "../../../../images/SideBar/Edit";
import ShareIcon from "../../../../images/SideBar/Share";
import UserPlayIcon from "../../../../images/SideBar/UserPlay";
import VideoIcon from "../../../../images/SideBar/Video";
import ViewFileImg from "../../../../images/SideBar/viewFile";
import { formatDate, style, stylePriority } from "../../../../util/constant";
import {
  IEmployee,
  IEmployeeWithProjects,
  IProject,
  IProjectWithTasks,
  ITask,
  ITaskSheetResponse,
} from "../Tasksheet.props";
import useTaskSheet from "../useTasksheet";
import EditTaskSheetIndex from "./EditTasksheet/index";
import ApiService from "../../../../service/ApiService";
import { apiPaths } from "../../../../service/apiPaths";
import { Column } from "../../../../components/CommonDataGrid/props";

// Interface for project-grouped data
interface IProjectGroupedData {
  project: IProject;
  tasks: (ITask & { assignedTo?: IEmployee })[];
}

// Date filter options like CommonDataGrid

const TaskSheet: React.FC = () => {
  const navigate = useNavigate();
  const {
    selectedTaskSheetId,
    taskSheetData,
    updateScreen,
    handleEdit,
    handleAdd,
    handleSelectStatus,
    loading,
    user,
    userId,
    projectId,
    setUserId,
    setProjectId,
    // Search and filter related
    searchValue,
    handleSearch,
    selectedDateFilter,
    handleDateFilterChange,
    // Accordion related
    expandedUsers,
    toggleUserAccordion,
    expandAllUsers,
    collapseAllUsers,
    // Custom date modal related
    showCustomDateModal,
    customFromDate,
    customToDate,
    setCustomFromDate,
    setCustomToDate,
    handleCustomDateSubmit,
    handleCustomDateCancel,
    getUserTotalTasks,
    getTotalTasks,
    clearAllFilters,
    dateFilterOptions,
    handleCopyAllData,
    handleVideoClick,
    showVideoModal,
    setShowVideoModal,
    selectedVideoUrl,
    setSelectedVideoUrl,
    project,
    statusFilter,
    setStatusFilter,
    statusFilterOptions,
    showCopyModal,
    selectedUserIds,
    toggleUserSelection,
    toggleSelectAll,
    handleConfirmCopy,
    handleCancelCopy,
  } = useTaskSheet();

  // const [showVideoModal, setShowVideoModal] = useState(false);
  // const [selectedVideoUrl, setSelectedVideoUrl] = useState("");
  const [projectSearch, setProjectSearch] = useState("");
  const [projectOptions, setProjectOptions] = useState<any[]>(project);
  const currentUserRole = localStorage.getItem("role");
  const currentUserId = localStorage.getItem("userId");
  const [activeTab, setActiveTab] = useState<
    "projects" | "subtasks" | "columns"
  >("projects");
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(
    new Set()
  );
  const [showClosedTasks, setShowClosedTasks] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [groupBy, setGroupBy] = useState<"user" | "project">("user");
  const [showGroupByDropdown, setShowGroupByDropdown] = useState(false);

  const isFinishedToday = (task: ITask) => {
    if (task.status !== "finished" || !task.updatedAt) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const updatedAt = new Date(task.updatedAt);
    updatedAt.setHours(0, 0, 0, 0);

    return updatedAt.getTime() === today.getTime();
  };

  const isFinishedBeforeToday = (task: ITask) => {
    if (task.status !== "finished" || !task.updatedAt) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const updatedAt = new Date(task.updatedAt);
    updatedAt.setHours(0, 0, 0, 0);

    return updatedAt < today;
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const result = await ApiService({
          method: "GET",
          endpoint: `${apiPaths.getProjects}?search=${projectSearch}`,
        });
        setProjectOptions([{ id: null, name: "select Project" }, ...result]);
      } catch (error) {
        setProjectOptions([{ id: null, name: "select Project" }]);
      }
    };
    if (projectSearch) {
      fetchProjects();
    } else {
      setProjectOptions(project);
    }
  }, [projectSearch, project]);

  // User grouped data (default structure - already grouped by user)
  const userGroupedData = useMemo(() => {
    return taskSheetData.map((employeeData: IEmployeeWithProjects) => {
      // Flatten all tasks from all projects for this user
      const allTasks = employeeData.projects.flatMap(
        (projectData: IProjectWithTasks) =>
          projectData.tasks.map((task: ITask) => ({
            ...task,
            assignedTo: employeeData.employee,
            project: projectData.project,
          }))
      );
      return {
        employee: employeeData.employee,
        tasks: allTasks,
      };
    });
  }, [taskSheetData]);

  // Project grouped data
  const projectGroupedData = useMemo(() => {
    const projectMap = new Map<string, IProjectGroupedData>();

    taskSheetData.forEach((employeeData: IEmployeeWithProjects) => {
      employeeData.projects.forEach((projectData: IProjectWithTasks) => {
        const projectId = projectData.project._id;

        if (!projectMap.has(projectId)) {
          projectMap.set(projectId, {
            project: projectData.project,
            tasks: [],
          });
        }

        const projectGroup = projectMap.get(projectId)!;
        projectData.tasks.forEach((task: ITask) => {
          projectGroup.tasks.push({
            ...task,
            assignedTo: employeeData.employee,
          });
        });
      });
    });

    // Sort projects: projects with tasks first, then projects with no tasks
    const projectsArray = Array.from(projectMap.values());
    return projectsArray.sort((a, b) => {
      // If both have tasks or both have no tasks, maintain original order
      if (
        (a.tasks.length > 0 && b.tasks.length > 0) ||
        (a.tasks.length === 0 && b.tasks.length === 0)
      ) {
        return 0;
      }
      // Projects with tasks come first (return -1), projects without tasks come last (return 1)
      return a.tasks.length > 0 ? -1 : 1;
    });
  }, [taskSheetData]);

  useEffect(() => {
    if (
      groupBy === "project" &&
      projectGroupedData.length > 0 &&
      expandedProjects.size === 0
    ) {
      const allProjectIds = projectGroupedData.map((p) => p.project._id);
      setExpandedProjects(new Set(allProjectIds));
    }
  }, [projectGroupedData, groupBy]);

  useEffect(() => {
    if (
      groupBy === "user" &&
      userGroupedData.length > 0 &&
      expandedUsers.size === 0
    ) {
      // Use the hook's expandAllUsers function
      expandAllUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userGroupedData, groupBy]);

  const toggleProjectAccordion = (projectId: string) => {
    const newExpandedProjects = new Set(expandedProjects);
    if (newExpandedProjects.has(projectId)) {
      newExpandedProjects.delete(projectId);
    } else {
      newExpandedProjects.add(projectId);
    }
    setExpandedProjects(newExpandedProjects);
  };

  const expandAllProjects = () => {
    const allProjectIds = projectGroupedData.map((p) => p.project._id);
    setExpandedProjects(new Set(allProjectIds));
  };

  const collapseAllProjects = () => {
    setExpandedProjects(new Set());
  };

  // Local expand/collapse functions for user grouping that work with the hook's state
  const expandAllUsersLocal = () => {
    const allUserIds = userGroupedData.map((u) => u.employee._id);
    // Use the hook's expandAllUsers function
    const newExpandedUsers = new Set(allUserIds);
    // We need to manually set the expanded users since the hook's function might work differently
    // For now, we'll use the hook's function but we may need to adjust
    expandAllUsers();
  };

  const collapseAllUsersLocal = () => {
    collapseAllUsers();
  };

  const getProjectColor = (projectName: string) => {
    const colors = [
      "bg-blue-100 text-blue-800",
      "bg-orange-100 text-orange-800",
      "bg-green-100 text-green-800",
      "bg-purple-100 text-purple-800",
      "bg-pink-100 text-pink-800",
      "bg-indigo-100 text-indigo-800",
    ];
    const index = projectName.length % colors.length;
    return colors[index];
  };

  const getStatusCircleColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "todo":
        return "bg-gray-300 border-gray-400";
      case "in progress":
        return "bg-orange-400 border-orange-500";
      case "query":
        return "bg-red-700 border-red-800";
      case "finished":
        return "bg-green-400 border-green-500";
      case "backlog":
        return "bg-rose-500 border-rose-600";
      default:
        return "bg-gray-300 border-gray-400";
    }
  };

  const getUserColor = (userName: string) => {
    const colors = [
      "bg-blue-100 text-blue-800",
      "bg-orange-100 text-orange-800",
      "bg-green-100 text-green-800",
      "bg-purple-100 text-purple-800",
      "bg-pink-100 text-pink-800",
      "bg-indigo-100 text-indigo-800",
    ];
    const index = userName.length % colors.length;
    return colors[index];
  };

  // Check if a task is overdue
  const isTaskOverdue = (task: ITask) => {
    if (!task.dueDate) return false;
    if (task.status === "finished") return false; // Finished tasks are not overdue
    if (task.status === "backLog") return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dueDate = new Date(task.dueDate);
    dueDate.setHours(0, 0, 0, 0);

    // Task is overdue if dueDate is before today
    return dueDate < today;
  };

  return (
    <div className="min-h-[70vh] bg-gray-50">
      <div className="mx-4 lg:mx-6">
        {loading && <Loader />}
        <div>
          {selectedTaskSheetId ? (
            <EditTaskSheetIndex
              taskSheetId={selectedTaskSheetId}
              setMethod={() => updateScreen("edit")}
            />
          ) : (
            <>
              {/* Tabs Section */}
              {/* <div className="bg-white border-b border-gray-200 mb-4">
                <div className="flex items-center space-x-6">
                  <button
                    onClick={() => setActiveTab("projects")}
                    className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                      activeTab === "projects"
                        ? "border-blue-500 text-blue-600 font-medium"
                        : "border-transparent text-gray-600 hover:text-gray-800"
                    }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Group: Projects
                  </button>
                  <button
                    onClick={() => setActiveTab("subtasks")}
                    className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                      activeTab === "subtasks"
                        ? "border-blue-500 text-blue-600 font-medium"
                        : "border-transparent text-gray-600 hover:text-gray-800"
                    }`}
                  >
                    Subtasks
                  </button>
                  <button
                    onClick={() => setActiveTab("columns")}
                    className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                      activeTab === "columns"
                        ? "border-blue-500 text-blue-600 font-medium"
                        : "border-transparent text-gray-600 hover:text-gray-800"
                    }`}
                  >
                    Columns
                  </button>
                </div>
              </div> */}

              {/* Filter Section */}
              <div className="bg-white border-b border-gray-200 mb-4">
                <div className="flex items-center justify-between py-3 px-4">
                  <div className="flex items-center gap-3">
                    {/* Group By Dropdown */}
                    <div className="relative">
                      <button
                        onClick={() =>
                          setShowGroupByDropdown(!showGroupByDropdown)
                        }
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors text-gray-700 hover:bg-gray-100"
                      >
                        <Folder size={16} />
                        Group by:{" "}
                        {groupBy === "user" ? "Assigned To" : "Project"}
                        <ChevronDown
                          size={14}
                          className={
                            showGroupByDropdown ? "transform rotate-180" : ""
                          }
                        />
                      </button>
                      {showGroupByDropdown && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setShowGroupByDropdown(false)}
                          ></div>
                          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-20 min-w-[160px]">
                            <button
                              onClick={() => {
                                setGroupBy("user");
                                setShowGroupByDropdown(false);
                              }}
                              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                                groupBy === "user"
                                  ? "bg-blue-50 text-blue-700 font-medium"
                                  : "text-gray-700"
                              }`}
                            >
                              Assigned To
                            </button>
                            <button
                              onClick={() => {
                                setGroupBy("project");
                                setShowGroupByDropdown(false);
                              }}
                              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                                groupBy === "project"
                                  ? "bg-blue-50 text-blue-700 font-medium"
                                  : "text-gray-700"
                              }`}
                            >
                              Project
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                    <button
                      onClick={() =>
                        setShowAdvancedFilters(!showAdvancedFilters)
                      }
                      className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        showAdvancedFilters
                          ? "bg-blue-100 text-blue-700"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <Filter size={16} />
                      Filter
                    </button>
                    <button
                      onClick={() => setShowClosedTasks(!showClosedTasks)}
                      className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        showClosedTasks
                          ? "bg-blue-100 text-blue-700"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <CheckCircle2 size={16} />
                      Closed
                    </button>
                    {userId && (
                      <button className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors">
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-semibold">
                            {user
                              .find((u) => u.id === userId)
                              ?.name?.charAt(0)
                              .toUpperCase() || "A"}
                          </span>
                        </div>
                        <span>
                          Assignee{" "}
                          {user.find((u) => u.id === userId)?.name?.charAt(0) ||
                            "A"}
                        </span>
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search
                        size={16}
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="text"
                        placeholder="Search..."
                        value={searchValue}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="w-64 h-8 pl-10 pr-3 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
                      />
                    </div>
                  </div>
                </div>

                {/* Advanced Filters (Collapsible) */}
                {showAdvancedFilters && (
                  <div className="px-4 pb-3 border-t border-gray-200 pt-3">
                    <div className="flex gap-4">
                      {/* <div>
                        <CustomDropdown
                          label="Assigned To"
                          options={user}
                          defaultOptionId={userId || ""}
                          setDefaultOptionId={setUserId}
                          displayProperty="name"
                          value={userId || ""}
                          style="w-full h-8"
                          showStyle="text-xs"
                        />
                      </div>
                      <div>
                        <CustomDropdown
                          label="Project"
                          options={project}
                          defaultOptionId={projectId || ""}
                          setDefaultOptionId={setProjectId}
                          displayProperty="name"
                          value={projectId || ""}
                          style="w-full h-8"
                          showStyle="text-xs"
                        />
                      </div> */}
                      <div className="w-44">
                        <CustomDropdown
                          label="Status"
                          options={statusFilterOptions}
                          defaultOptionId={statusFilter}
                          setDefaultOptionId={setStatusFilter}
                          displayProperty="name"
                          value={statusFilter}
                          style="w-full h-8"
                          showStyle="text-xs "
                        />
                      </div>
                      <div className="w-44">
                        <CustomDropdown
                          label="Date"
                          options={dateFilterOptions}
                          defaultOptionId={selectedDateFilter}
                          setDefaultOptionId={handleDateFilterChange}
                          displayProperty="name"
                          value={selectedDateFilter}
                          style="w-full h-8"
                          showStyle="text-xs"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col mt-1 sm:flex-row justify-between items-center mb-3 gap-4">
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleCopyAllData}
                    className="bg-blue-500 flex gap-2 text-white p-2 rounded-lg hover:bg-blue-600 transition-colors duration-200 text-xs"
                  >
                    <CopyIcon size={16} /> Copy Data
                  </button>
                  <button
                    onClick={
                      groupBy === "project"
                        ? expandedProjects.size === 0
                          ? expandAllProjects
                          : collapseAllProjects
                        : expandedUsers.size === 0
                        ? expandAllUsersLocal
                        : collapseAllUsersLocal
                    }
                    className="inline-flex items-center gap-2 h-9 p-2 font-medium border border-gray-300 rounded-md hover:bg-gray-200 transition text-xs"
                  >
                    {groupBy === "project" ? (
                      expandedProjects.size === 0 ? (
                        <p className="flex items-center">
                          <ChevronsUpDown size={16} /> <span>Expand</span>
                        </p>
                      ) : (
                        <p className="flex items-center">
                          <ChevronsDownUp size={16} />
                          <span> Collapse</span>
                        </p>
                      )
                    ) : expandedUsers.size === 0 ? (
                      <p className="flex items-center">
                        <ChevronsUpDown size={16} /> <span>Expand</span>
                      </p>
                    ) : (
                      <p className="flex items-center">
                        <ChevronsDownUp size={16} />
                        <span> Collapse</span>
                      </p>
                    )}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    Total Tasks: {getTotalTasks()}
                  </span>
                </div>
              </div>

              <div className="overflow-y-auto h-[65vh] border-t-2 rounded-lg py-2 pb-8">
                {groupBy === "user" ? (
                  // User-wise grouping
                  userGroupedData.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full bg-white rounded-xl shadow-lg">
                      <div className="text-center">
                        <div className="text-6xl mb-4">📋</div>
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">
                          No Tasks Found
                        </h3>
                        <p className="text-gray-600 mb-4">
                          {loading
                            ? "Loading tasks..."
                            : "No tasks match your current filters. Try adjusting your search criteria."}
                        </p>
                        {!loading && (
                          <button
                            onClick={clearAllFilters}
                            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors duration-200"
                          >
                            Clear Filters
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4 pb-4">
                      {userGroupedData.map((userGroup) => {
                        const isExpanded = expandedUsers.has(
                          userGroup.employee._id
                        );
                        const filteredTasks = showClosedTasks
                          ? userGroup.tasks.filter((task) =>
                              isFinishedBeforeToday(task)
                            )
                          : userGroup.tasks.filter(
                              (task) =>
                                task.status !== "finished" ||
                                isFinishedToday(task)
                            );

                        const taskCount = filteredTasks.length;

                        return (
                          <div
                            key={userGroup.employee._id}
                            className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                          >
                            {/* User Header */}
                            <div
                              className="bg-white px-4 py-3 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
                              onClick={() =>
                                toggleUserAccordion(userGroup.employee._id)
                              }
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3 flex-1">
                                  {isExpanded ? (
                                    <ChevronDown className="w-5 h-5 text-gray-500" />
                                  ) : (
                                    <ChevronDown className="w-5 h-5 text-gray-400 transform -rotate-90" />
                                  )}
                                  <span
                                    className={`px-3 py-1 rounded-full text-sm font-medium ${getUserColor(
                                      userGroup.employee.name
                                    )}`}
                                  >
                                    {userGroup.employee.name}
                                  </span>
                                  <span className="text-sm text-gray-600">
                                    {taskCount} task{taskCount !== 1 ? "s" : ""}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Handle more options
                                    }}
                                    className="ml-auto text-gray-400 hover:text-gray-600"
                                  >
                                    <MoreVertical size={16} />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAdd();
                                    }}
                                    className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-md transition-colors"
                                  >
                                    <Plus size={16} />
                                    Add Task
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Tasks Table */}
                            {isExpanded && (
                              <div className="overflow-x-auto">
                                {filteredTasks.length > 0 ? (
                                  <table className="w-full text-sm">
                                    <thead className="bg-gray-50 border-b border-gray-200">
                                      <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          Name
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          Project
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          Status
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          Completed Today
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          Due date
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          Priority
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          Actions
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {filteredTasks.map(
                                        (
                                          task: ITask & {
                                            assignedTo?: IEmployee;
                                            project?: IProject;
                                          }
                                        ) => (
                                          <tr
                                            key={task._id}
                                            className="hover:bg-gray-50 transition-colors duration-200"
                                          >
                                            <td className="px-4 py-2">
                                              <div className="flex items-center gap-2">
                                                <div
                                                  className={`w-2 h-2 rounded-full border ${getStatusCircleColor(
                                                    task.status
                                                  )}`}
                                                ></div>
                                                <div>
                                                  <div className="font-medium text-gray-900 w-96 break-words">
                                                    {task.description}
                                                  </div>
                                                </div>
                                              </div>
                                            </td>
                                            <td className="px-4 py-2 text-gray-500">
                                              <span className="text-xs text-gray-500">
                                                {task.project?.name || "N/A"}
                                              </span>
                                            </td>
                                            <td className="px-4 py-2">
                                              <div className="flex items-center gap-2">
                                                <div
                                                  className={`${
                                                    style[task.status] ||
                                                    "bg-gray-200"
                                                  } rounded-lg p-2 inline-block`}
                                                >
                                                  <select
                                                    defaultValue={task.status}
                                                    onChange={(event) =>
                                                      handleSelectStatus(
                                                        event,
                                                        task._id
                                                      )
                                                    }
                                                    className="appearance-none bg-transparent outline-none cursor-pointer font-semibold text-xs"
                                                  >
                                                    <option value="todo">
                                                      To Do
                                                    </option>
                                                    <option value="in progress">
                                                      In Progress
                                                    </option>
                                                    <option value="query">
                                                      Query
                                                    </option>
                                                    <option value="finished">
                                                      Finished
                                                    </option>
                                                    <option value="backLog">
                                                      Back Log
                                                    </option>
                                                  </select>
                                                </div>
                                                {isTaskOverdue(task) && (
                                                  <span className="px-2 py-1 bg-red-500 text-white rounded-full text-xs font-semibold border border-red-800 whitespace-nowrap">
                                                    Overdue
                                                  </span>
                                                )}
                                              </div>
                                            </td>
                                            <td className="px-4 py-2">
                                              {isFinishedToday(task) ? (
                                                <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold border border-green-300">
                                                  ✔ Done Today
                                                </span>
                                              ) : (
                                                <span className="text-gray-300 text-xs">
                                                  —
                                                </span>
                                              )}
                                            </td>

                                            <td className="px-4 py-2 text-gray-900">
                                              <div className="font-medium">
                                                {formatDate(task.dueDate)}
                                              </div>
                                            </td>
                                            <td className="px-4 py-2">
                                              {task.priority === "urgent" ? (
                                                <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500">
                                                  <Flag
                                                    size={14}
                                                    className="fill-red-500 text-red-500"
                                                  />
                                                  Urgent
                                                </span>
                                              ) : task.priority === "high" ? (
                                                <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500">
                                                  <Flag
                                                    size={14}
                                                    className="fill-yellow-500 text-yellow-500"
                                                  />
                                                  High
                                                </span>
                                              ) : (
                                                <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500">
                                                  <Flag
                                                    size={14}
                                                    className="text-gray-500"
                                                  />
                                                  Low
                                                </span>
                                              )}
                                            </td>

                                            <td className="px-4 py-2">
                                              <div className="flex space-x-2">
                                                {task.videoUrl && (
                                                  <button
                                                    onClick={() =>
                                                      handleVideoClick(
                                                        task.videoUrl
                                                      )
                                                    }
                                                    className="text-blue-600 hover:text-blue-700 transition-colors duration-200"
                                                    title="View Video"
                                                  >
                                                    <VideoIcon />
                                                  </button>
                                                )}
                                                {task.userVideoUrl && (
                                                  <button
                                                    onClick={() =>
                                                      handleVideoClick(
                                                        task.userVideoUrl!
                                                      )
                                                    }
                                                    className="text-green-600 hover:text-green-700 transition-colors duration-200"
                                                    title="View User Video"
                                                  >
                                                    <UserPlayIcon />
                                                  </button>
                                                )}
                                                <button
                                                  onClick={() =>
                                                    handleEdit(task._id)
                                                  }
                                                  className="text-fontColor hover:text-primary-2 transition-colors duration-200"
                                                  title="Edit Task"
                                                >
                                                  <EditImg />
                                                </button>
                                                <button
                                                  onClick={() => {
                                                    navigate(
                                                      `/task/${task._id}`
                                                    );
                                                  }}
                                                  className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
                                                  title="View Task"
                                                >
                                                  <ViewFileImg />
                                                </button>
                                                <button
                                                  onClick={() => {
                                                    const taskUrl = `https://task.blisslers.com/task/${
                                                      task._id || ""
                                                    }`;
                                                    copy(taskUrl);
                                                    toast.success(
                                                      "Task URL copied to clipboard!"
                                                    );
                                                  }}
                                                  className="text-green-600 hover:text-green-800 transition-colors duration-200"
                                                  title="Share Task"
                                                >
                                                  <ShareIcon />
                                                </button>
                                              </div>
                                            </td>
                                          </tr>
                                        )
                                      )}
                                    </tbody>
                                  </table>
                                ) : (
                                  <div className="p-8 text-center">
                                    <div className="text-4xl mb-2">📝</div>
                                    <p className="text-gray-500">
                                      No tasks found for this user
                                    </p>
                                  </div>
                                )}
                                {/* Add Task Button at bottom */}
                                {filteredTasks.length > 0 && (
                                  <div className="px-4 py-3 border-t border-gray-200 mb-4">
                                    <button
                                      onClick={handleAdd}
                                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                                    >
                                      <Plus size={16} />
                                      Add Task
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )
                ) : // Project-wise grouping (existing code)
                projectGroupedData.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full bg-white rounded-xl shadow-lg">
                    <div className="text-center">
                      <div className="text-6xl mb-4">📋</div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-2">
                        No Tasks Found
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {loading
                          ? "Loading tasks..."
                          : "No tasks match your current filters. Try adjusting your search criteria."}
                      </p>
                      {!loading && (
                        <button
                          onClick={clearAllFilters}
                          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors duration-200"
                        >
                          Clear Filters
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 pb-4">
                    {projectGroupedData.map(
                      (projectGroup: IProjectGroupedData) => {
                        const isExpanded = expandedProjects.has(
                          projectGroup.project._id
                        );
                        const filteredTasks = showClosedTasks
                          ? projectGroup.tasks.filter((task) =>
                              isFinishedBeforeToday(task)
                            )
                          : projectGroup.tasks.filter(
                              (task) =>
                                task.status !== "finished" ||
                                isFinishedToday(task)
                            );

                        const taskCount = filteredTasks.length;

                        return (
                          <div
                            key={projectGroup.project._id}
                            className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                          >
                            {/* Project Header */}
                            <div
                              className="bg-white px-4 py-3 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
                              onClick={() =>
                                toggleProjectAccordion(projectGroup.project._id)
                              }
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3 flex-1">
                                  {isExpanded ? (
                                    <ChevronDown className="w-5 h-5 text-gray-500" />
                                  ) : (
                                    <ChevronDown className="w-5 h-5 text-gray-400 transform -rotate-90" />
                                  )}
                                  <span
                                    className={`px-3 py-1 rounded-full text-sm font-medium ${getProjectColor(
                                      projectGroup.project.name
                                    )}`}
                                  >
                                    {projectGroup.project.name}
                                  </span>
                                  <span className="text-sm text-gray-600">
                                    {taskCount} task{taskCount !== 1 ? "s" : ""}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Handle more options
                                    }}
                                    className="ml-auto text-gray-400 hover:text-gray-600"
                                  >
                                    <MoreVertical size={16} />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAdd();
                                    }}
                                    className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-md transition-colors"
                                  >
                                    <Plus size={16} />
                                    Add Task
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Tasks Table */}
                            {isExpanded && (
                              <div className="overflow-x-auto">
                                {filteredTasks.length > 0 ? (
                                  <table className="w-full text-sm">
                                    <thead className="bg-gray-50 border-b border-gray-200">
                                      <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          Name
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          Assignee
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          Status
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          Due date
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          Priority
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          Actions
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {filteredTasks.map(
                                        (
                                          task: ITask & {
                                            assignedTo?: IEmployee;
                                          }
                                        ) => (
                                          <tr
                                            key={task._id}
                                            className="hover:bg-gray-50 transition-colors duration-200"
                                          >
                                            <td className="px-4 py-2">
                                              <div className="flex items-center gap-2">
                                                <div
                                                  className={`w-2 h-2 rounded-full border ${getStatusCircleColor(
                                                    task.status
                                                  )}`}
                                                ></div>
                                                <div>
                                                  <div className="font-medium text-gray-900 w-96 break-words">
                                                    {task.description}
                                                  </div>
                                                </div>
                                              </div>
                                            </td>
                                            <td className="px-4 py-2 text-gray-500">
                                              <span className="text-gray-400">
                                                {task.assignedTo && (
                                                  <div className="text-xs text-gray-500 mt-1">
                                                    {task.assignedTo.name}
                                                  </div>
                                                )}
                                              </span>
                                            </td>
                                            <td className="px-4 py-2">
                                              <div className="flex items-center gap-2">
                                                <div
                                                  className={`${
                                                    style[task.status] ||
                                                    "bg-gray-200"
                                                  } rounded-lg p-2 inline-block`}
                                                >
                                                  <select
                                                    defaultValue={task.status}
                                                    onChange={(event) =>
                                                      handleSelectStatus(
                                                        event,
                                                        task._id
                                                      )
                                                    }
                                                    className="appearance-none bg-transparent outline-none cursor-pointer font-semibold text-xs"
                                                  >
                                                    <option value="todo">
                                                      To Do
                                                    </option>
                                                    <option value="in progress">
                                                      In Progress
                                                    </option>
                                                    <option value="query">
                                                      Query
                                                    </option>
                                                    <option value="finished">
                                                      Finished
                                                    </option>
                                                    <option value="backLog">
                                                      Back Log
                                                    </option>
                                                  </select>
                                                </div>
                                                {isTaskOverdue(task) && (
                                                  <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold border border-red-300 whitespace-nowrap">
                                                    Overdue
                                                  </span>
                                                )}
                                              </div>
                                            </td>
                                            <td className="px-4 py-2 text-gray-900">
                                              <div className="font-medium">
                                                {formatDate(task.dueDate)}
                                              </div>
                                            </td>
                                            <td className="px-4 py-2">
                                              {task.priority === "urgent" ? (
                                                <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500">
                                                  <Flag
                                                    size={14}
                                                    className="fill-red-500 text-red-500"
                                                  />
                                                  Urgent
                                                </span>
                                              ) : task.priority === "high" ? (
                                                <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500">
                                                  <Flag
                                                    size={14}
                                                    className="fill-yellow-500 text-yellow-500"
                                                  />
                                                  High
                                                </span>
                                              ) : (
                                                <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500">
                                                  <Flag
                                                    size={14}
                                                    className="text-gray-500"
                                                  />
                                                  Low
                                                </span>
                                              )}
                                            </td>

                                            <td className="px-4 py-2">
                                              <div className="flex space-x-2">
                                                {task.videoUrl && (
                                                  <button
                                                    onClick={() =>
                                                      handleVideoClick(
                                                        task.videoUrl
                                                      )
                                                    }
                                                    className="text-blue-600 hover:text-blue-700 transition-colors duration-200"
                                                    title="View Video"
                                                  >
                                                    <VideoIcon />
                                                  </button>
                                                )}
                                                {task.userVideoUrl && (
                                                  <button
                                                    onClick={() =>
                                                      handleVideoClick(
                                                        task.userVideoUrl!
                                                      )
                                                    }
                                                    className="text-green-600 hover:text-green-700 transition-colors duration-200"
                                                    title="View User Video"
                                                  >
                                                    <UserPlayIcon />
                                                  </button>
                                                )}
                                                <button
                                                  onClick={() =>
                                                    handleEdit(task._id)
                                                  }
                                                  className="text-fontColor hover:text-primary-2 transition-colors duration-200"
                                                  title="Edit Task"
                                                >
                                                  <EditImg />
                                                </button>
                                                <button
                                                  onClick={() => {
                                                    navigate(
                                                      `/task/${task._id}`
                                                    );
                                                  }}
                                                  className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
                                                  title="View Task"
                                                >
                                                  <ViewFileImg />
                                                </button>
                                                <button
                                                  onClick={() => {
                                                    const taskUrl = `https://task.blisslers.com/task/${
                                                      task._id || ""
                                                    }`;
                                                    copy(taskUrl);
                                                    toast.success(
                                                      "Task URL copied to clipboard!"
                                                    );
                                                  }}
                                                  className="text-green-600 hover:text-green-800 transition-colors duration-200"
                                                  title="Share Task"
                                                >
                                                  <ShareIcon />
                                                </button>
                                              </div>
                                            </td>
                                          </tr>
                                        )
                                      )}
                                    </tbody>
                                  </table>
                                ) : (
                                  <div className="p-8 text-center">
                                    <div className="text-4xl mb-2">📝</div>
                                    <p className="text-gray-500">
                                      No tasks found for this project
                                    </p>
                                  </div>
                                )}
                                {/* Add Task Button at bottom */}
                                {filteredTasks.length > 0 && (
                                  <div className="px-4 py-3 border-t border-gray-200 mb-4">
                                    <button
                                      onClick={handleAdd}
                                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                                    >
                                      <Plus size={16} />
                                      Add Task
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
              </div>

              {/* Custom Date Modal */}
              {showCustomDateModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                  <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-800">
                        Select Custom Date Range
                      </h3>
                      <button
                        onClick={handleCustomDateCancel}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <svg
                          className="w-6 h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          From Date:
                        </label>
                        <input
                          type="date"
                          value={
                            customFromDate
                              ? customFromDate.toISOString().split("T")[0]
                              : ""
                          }
                          onChange={(e) => {
                            const date = e.target.value
                              ? new Date(e.target.value)
                              : null;
                            setCustomFromDate(date);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          To Date:
                        </label>
                        <input
                          type="date"
                          value={
                            customToDate
                              ? customToDate.toISOString().split("T")[0]
                              : ""
                          }
                          onChange={(e) => {
                            const date = e.target.value
                              ? new Date(e.target.value)
                              : null;
                            setCustomToDate(date);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end space-x-3 mt-6">
                      <button
                        onClick={handleCustomDateCancel}
                        className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCustomDateSubmit}
                        disabled={!customFromDate || !customToDate}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                      >
                        Apply Filter
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Copy Data Modal */}
      {showCopyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Select Users to Copy
              </h3>
              <button
                onClick={handleCancelCopy}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Select All / Deselect All Checkbox */}
            <div className="mb-4 border-b border-gray-200">
              <div
                className="flex items-center p-4 cursor-pointer transition-all duration-200"
                onClick={toggleSelectAll}
              >
                <input
                  type="checkbox"
                  checked={
                    selectedUserIds.size === taskSheetData.length &&
                    taskSheetData.length > 0
                  }
                  onChange={toggleSelectAll}
                  className="w-5 h-5 rounded focus:ring-blue-500 cursor-pointer"
                />
                <div className="ml-2 flex-1">
                  <h4 className="text-base font-semibold text-gray-800">
                    {selectedUserIds.size === taskSheetData.length
                      ? "Deselect All"
                      : "Select All"}
                  </h4>
                  {/* <p className="text-sm text-gray-600 mt-1">
                    {selectedUserIds.size} of {taskSheetData.length} user(s) selected
                  </p> */}
                </div>
              </div>
            </div>

            {/* User Checkboxes */}
            <div className="grid grid-cols-2 space-y-2 gap-4 mb-4">
              {taskSheetData.map((employeeData: IEmployeeWithProjects) => {
                const isSelected = selectedUserIds.has(
                  employeeData.employee._id
                );
                const totalTasks = getUserTotalTasks(employeeData);

                return (
                  <div
                    key={employeeData.employee._id}
                    className={`flex items-center p-2 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
                      isSelected
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                    onClick={() =>
                      toggleUserSelection(employeeData.employee._id)
                    }
                  >
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() =>
                          toggleUserSelection(employeeData.employee._id)
                        }
                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                      />
                      <div className="ml-4 flex items-center space-x-4 flex-1">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-blue-600 font-semibold">
                            {employeeData.employee.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-base font-semibold text-gray-800">
                            {employeeData.employee.name}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">
                            {totalTasks} task{totalTasks !== 1 ? "s" : ""} •{" "}
                            {employeeData.projects.length} project
                            {employeeData.projects.length !== 1 ? "s" : ""}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                onClick={handleCancelCopy}
                className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmCopy}
                disabled={selectedUserIds.size === 0}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
              >
                Copy Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      <VideoModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        videoUrl={selectedVideoUrl}
        title="Task Video"
      />
    </div>
  );
};

export default TaskSheet;
